import React, { useState } from 'react';
import { Container, Form, Button, Alert, Card } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import axios from 'axios';

export type PadColorKeys = 'red' | 'pink' | 'orange' | 'yellow' | 'green' | 'blue' | 'purple';

// Validation schema
const deviceConnectionSchema = z.object({
  deviceName: z.string()
    .min(2, 'Device name must be at least 2 characters')
    .max(50, 'Device name must be less than 50 characters')
    .regex(/^[a-zA-Z0-9\s\-_]+$/, 'Device name can only contain letters, numbers, spaces, hyphens, and underscores'),
  deviceSerial: z.string()
    .min(6, 'Serial number must be at least 6 characters')
    .max(20, 'Serial number must be less than 20 characters')
    .regex(/^[A-Z0-9]+$/, 'Serial number must be uppercase letters and numbers only')
    .transform(val => val.toUpperCase().trim()),
  location: z.string()
    .length(3, 'Location code must be exactly 3 characters')
    .regex(/^[A-Z]+$/, 'Location code must be uppercase letters only')
    .transform(val => val.toUpperCase().trim()),
  locationColor: z.enum(['red', 'pink', 'orange', 'yellow', 'green', 'blue', 'purple'] as const),
});

type DeviceConnectionFormData = z.infer<typeof deviceConnectionSchema>;

const DeviceConnectionPage: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const form = useForm<DeviceConnectionFormData>({
    resolver: zodResolver(deviceConnectionSchema),
    defaultValues: {
      locationColor: 'blue',
    },
  });

  const onSubmit = async (data: DeviceConnectionFormData) => {
    try {
      setIsLoading(true);
      setError(null);
      await axios.post('/api/devices/connect', data);
      setSuccess(true);
      form.reset();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to connect device');
    } finally {
      setIsLoading(false);
    }
  };

  const colorOptions: { value: PadColorKeys; label: string }[] = [
    { value: 'red', label: 'Red' },
    { value: 'pink', label: 'Pink' },
    { value: 'orange', label: 'Orange' },
    { value: 'yellow', label: 'Yellow' },
    { value: 'green', label: 'Green' },
    { value: 'blue', label: 'Blue' },
    { value: 'purple', label: 'Purple' },
  ];

  const getColorBoxStyle = (color: PadColorKeys) => ({
    width: '24px',
    height: '24px',
    backgroundColor: color,
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    display: 'inline-block',
    marginLeft: '8px',
    verticalAlign: 'middle',
  });

  return (
    <Container className="py-5">
      <Card className="mx-auto" style={{ maxWidth: '500px' }}>
        <Card.Body>
          <h3 className="text-center mb-4">Connect Device to Organization</h3>
          
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" className="mb-4">
              Device connected successfully!
            </Alert>
          )}

          <Form onSubmit={form.handleSubmit(onSubmit)}>
            <Form.Group className="mb-3">
              <Form.Label>Device Name</Form.Label>
              <Form.Control
                type="text"
                {...form.register('deviceName')}
                isInvalid={!!form.formState.errors.deviceName}
                placeholder="e.g., Main Reception Device"
              />
              <Form.Control.Feedback type="invalid">
                {form.formState.errors.deviceName?.message}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                A friendly name to identify this device in your organization.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Device Serial Number</Form.Label>
              <Form.Control
                type="text"
                {...form.register('deviceSerial')}
                isInvalid={!!form.formState.errors.deviceSerial}
                placeholder="e.g., ABC123XYZ"
              />
              <Form.Control.Feedback type="invalid">
                {form.formState.errors.deviceSerial?.message}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                The unique serial number found on your device.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Location Code</Form.Label>
              <Form.Control
                type="text"
                {...form.register('location')}
                isInvalid={!!form.formState.errors.location}
                placeholder="e.g., REC"
                maxLength={3}
                style={{ textTransform: 'uppercase' }}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  form.setValue('location', value);
                }}
              />
              <Form.Control.Feedback type="invalid">
                {form.formState.errors.location?.message}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                A 3-letter code for quick reference (e.g., REC for Reception, LAB for Laboratory).
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Location Color</Form.Label>
              <div className="d-flex align-items-center">
                <Form.Select
                  {...form.register('locationColor')}
                  isInvalid={!!form.formState.errors.locationColor}
                  style={{ flex: 1 }}
                >
                  {colorOptions.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Form.Select>
                <div style={getColorBoxStyle(form.watch('locationColor'))} />
              </div>
              <Form.Control.Feedback type="invalid">
                {form.formState.errors.locationColor?.message}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                Choose a color to represent this device's location.
              </Form.Text>
            </Form.Group>

            <Alert variant="info" className="mb-4">
              <small>
                Note: Device name, location code, and color can be modified later from the Devices page.
              </small>
            </Alert>

            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                type="submit" 
                disabled={isLoading}
              >
                {isLoading ? 'Connecting...' : 'Connect Device'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default DeviceConnectionPage; 